<template>
  <Loading v-if="isLoading" :message="loadingMessage" />
  <Header v-if="isLogged || isGuestMode" ref="headerRef" :guest-mode="isGuestMode"
          @toggle-navbar="toggleNavbar" @on-search="onSearch" />
  <NavBar v-if="showNavBar" :is-navbar-active="isNavbarActive"/>
  <div :class="{'page-content': isLogged, 'guest-mode-page-content': isGuestMode, 'width-100p': isFullMapOpen}">
    <ContentHolder v-if="essentialDataLoaded" :search-token="searchToken"/>
  </div>
  <events-list></events-list>
  <vue-notification-list position="top-right"></vue-notification-list>
</template>

<script>
import NavBar from '@/components/NavBar.vue';
import ContentHolder from '@/components/ContentHolder';
import {VueNotificationList} from '@dafcoe/vue-notification';
import {mapGetters, mapState} from 'vuex';
import Loading from '@/components/Loading';
import Header from '@/components/Header';
import {actions, mutations} from '@/assets/js/vue/vuex/actions';
import {ws} from '@/assets/js/vue/vuex/ws';
import notification from '@/assets/js/notification';
import EventsList from "@/components/pages/events/EventsList.vue";
import {entityDataManager, GoogleMapsManager} from "@/assets/js/data-manipulation";
import paths from "@/assets/js/vue/router/paths";

export default {
  name: 'App',
  components: {
    EventsList,
    Loading,
    Header,
    ContentHolder,
    NavBar,
    VueNotificationList,
  },
  data() {
    return {
      isNavbarActive: false,
      essentialDataLoaded: false,
      searchToken: '',
    };
  },
  computed: {
    ...mapState(['isLoading', 'loadingMessage', 'session', 'isGuestMode', 'newView']),
    ...mapGetters(['isLogged', 'isAdminUser']),
    showNavBar() {
      return this.isLogged && (!this.isFullMapOpen || this.isNavbarActive);
    },
    isFullMapOpen() {
      return this.isNewDashboardOpen || this.isOazisSuppliersMapOpen;
    },
    isNewDashboardOpen() {
      return this.isDashboardOpen && this.newView;
    },
    isDashboardOpen() {
      return this.$route.path === paths.home;
    },
    isOazisSuppliersMapOpen() {
      return this.$route.path.startsWith(paths.oazisSuppliersMap);
    },
  },
  watch: {
    $route() {
      this.isNavbarActive = false;
    },
    isLogged(newVal) {
      if (newVal === true) {
        this.initializeData();
      }
      else {
        this.cleanupData();
      }
    },
  },
  mounted() {
    GoogleMapsManager.loadGoogleMaps();
    if (!this.isLogged) {
      this.essentialDataLoaded = true;
    }

    this.initializeLocale();
  },
  methods: {
    initializeData() {
      this.$gtag.set({user_id: this.session.id});
      Promise.all([
        this.$store.dispatch(actions.GET_DEVICES),
        this.$store.dispatch(actions.GET_MAKES),
        this.$store.dispatch(actions.GET_MODELS),
        this.$store.dispatch(actions.GET_VEHICLES)
            .then(() => {
              if (this.isAdminUser) {
                return;
              }

              return this.$store.dispatch(actions.GET_MAINTENANCES_BY_USER);
            })
            .then(() => this.$store.dispatch(actions.SHOW_MAINTENANCE_NOTIFICATIONS)),
        this.$store.dispatch(actions.GET_DRIVERS),
        this.$store.dispatch(actions.GET_GEO_FENCES),
        this.$store.dispatch(actions.GET_USER_GEO_FENCES),
        this.$store.dispatch(actions.GET_GROUPS),
        this.$store.dispatch(actions.GET_TASKS),
        this.$store.dispatch(actions.GET_TASK_SCHEDULES),
        this.$store.dispatch(actions.GET_TRACCAR_MAINTENANCES_BY_USER),
        this.$store.dispatch(actions.GET_USER_COMPANY)
            .then(company => {
              if (!this.isAdminUser && entityDataManager.shouldFetchViolations(company)) {
                this.$store.dispatch(actions.CHECK_VIOLATIONS)
                    .catch(e => notification.error(this.$t(e.message)));
              }
            }),
        this.$store.dispatch(actions.GET_CURRENT_USER_ASSIGNED_EVENTS)
      ]).
          catch(e => notification.error(this.$t(e.message))).
          then(() => {
            if (!this.isAdminUser) {
              ws.init();
            }
            this.essentialDataLoaded = true;
          });
    },
    cleanupData() {
      this.$gtag.set({user_id: null});

      ws.close();
      this.$refs.headerRef.clearSearch();
      this.$store.commit(mutations.SET_DEVICES, []);
      this.$store.commit(mutations.SET_MAKES, []);
      this.$store.commit(mutations.SET_MODELS, []);
      this.$store.commit(mutations.SET_VEHICLES, []);
      this.$store.commit(mutations.SET_VIOLATIONS, []);
      this.$store.commit(mutations.SET_DRIVERS, []);
      this.$store.commit(mutations.SET_GEO_FENCES, []);
      this.$store.commit(mutations.SET_USER_GEO_FENCES, []);
      this.$store.commit(mutations.SET_GROUPS, []);
      this.$store.commit(mutations.CLEAR_DEVICE_DRIVERS);
      this.$store.commit(mutations.CLEAR_POSITIONS);
      this.$store.commit(mutations.CLEAR_EVENTS);
      this.$store.commit(mutations.SET_COMPANIES, []);
      this.$store.commit(mutations.SET_MAINTENANCES, []);
      this.$store.commit(mutations.SET_TRACCAR_MAINTENANCES, []);

      this.$store.commit(mutations.SET_TRIPS_FILTER_DATE_FROM, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_DATE_TO, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_DEVICE_ID, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_DRIVER, null);
      this.$store.commit(mutations.SET_TRIPS_FILTER_GROUP, null);
      this.$store.commit(mutations.SET_GARAGE_FILTER_DEVICE, []);
      this.$store.commit(mutations.SET_GARAGE_FILTER_STATUS, []);
      this.$store.commit(mutations.SET_TRIPS_DATA, []);
      this.$store.commit(mutations.SET_EVENTS_FILTER_DATE_FROM, null);
      this.$store.commit(mutations.SET_EVENTS_FILTER_DATE_TO, null);
      this.$store.commit(mutations.SET_EVENTS_FILTER_DEVICE_ID, []);
      this.$store.commit(mutations.SET_EVENTS_FILTER_STATUSES, []);
      this.$store.commit(mutations.SET_EVENTS_RECORDS, []);
      this.$store.commit(mutations.SET_CURRENT_USER_ASSIGNED_EVENTS, []);
      this.$store.commit(mutations.SET_TASKS, []);
      this.$store.commit(mutations.RESET_TASKS_FILTERS);
      this.$store.commit(mutations.SET_TASK_SCHEDULES, []);
      this.$store.commit(mutations.SET_USE_DEFAULT_DASHBOARD_VIEW);
    },
    initializeLocale() {
      this.$store.commit(mutations.SET_LOCALE, this.$i18n.locale);
    },
    toggleNavbar() {
      this.isNavbarActive = !this.isNavbarActive;
    },
    onSearch(searchToken) {
      this.searchToken = searchToken;
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

// Desktop:
@media (min-width: $breaking-point + 1) {
  .page-content {
    float: right;
    width: calc(100% - $desktop-sidebar-width);
  }

  .guest-mode-page-content {
    padding: 20px;
  }
}

// Mobile:
@media (max-width: $breaking-point) {
  .page-content .guest-mode-page-content {
    width: 100%;
  }
}
</style>
